<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useProfileStore } from '@/stores/profileStore';
import { useAuthStore } from '@/stores/authStore';
import OrgLogo from '@/components/sidebar/OrgLogo.vue';

// Design reference: docs/design/organization-profile-modal-handoff.md (Option B, "Split panel").
// Left rail: identity + the quiet identifiers block. Right pane: Contact and Billing address.

const profileStore = useProfileStore();
const authStore = useAuthStore();
const { organization, currentOrgId } = storeToRefs(authStore);
const { showProfileModal } = storeToRefs(profileStore);

const trimmed = (value?: string | null) => value?.trim() ?? '';

const websiteHref = computed(() => {
    const raw = trimmed(organization.value?.websiteUrl);
    if (!raw) return '';
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
});

const websiteLabel = computed(() =>
    trimmed(organization.value?.websiteUrl)
        .replace(/^https?:\/\//i, '')
        .replace(/\/$/, '')
);

const phone = computed(() => trimmed(organization.value?.phoneNumber));
const email = computed(() => trimmed(organization.value?.email));
const hasContact = computed(() => Boolean(phone.value || email.value));

/** The billing address as display lines, with every empty part dropped. */
const addressLines = computed(() => {
    const org = organization.value ?? {};
    const region = [trimmed(org.billingStateProvince), trimmed(org.billingPostalCode)].filter(Boolean).join(' ');
    const cityLine = [trimmed(org.billingCity), region].filter(Boolean).join(', ');
    return [trimmed(org.billingAddress1), trimmed(org.billingAddress2), cityLine, trimmed(org.billingCountry)].filter(Boolean);
});

const identifiers = computed(() =>
    [
        { key: 'id', label: 'Org ID', value: trimmed(organization.value?.organizationId), copyLabel: 'Copy organization ID' },
        { key: 'code', label: 'Org code', value: trimmed(currentOrgId.value), copyLabel: 'Copy organization code' }
    ].filter((item) => item.value)
);

const copiedKey = ref<string | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

const copy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    copiedKey.value = key;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
        copiedKey.value = null;
    }, 2000);
};

const close = () => {
    profileStore.closeProfileModal();
};
</script>

<template>
    <Dialog
        v-model:visible="showProfileModal"
        modal
        :draggable="false"
        :resizable="false"
        appendTo="body"
        :showHeader="false"
        :class="$style.dialog"
        :pt="{ content: { class: $style.content } }"
        aria-label="Organization Profile"
    >
        <div :class="$style.header">
            <h2 :class="$style.title">Organization Profile</h2>
            <Button icon="pi pi-times" text rounded severity="secondary" aria-label="Close" :class="$style.close" @click="close" />
        </div>

        <div :class="$style.body">
            <div :class="$style.rail">
                <OrgLogo>
                    <div :class="$style.identity">
                        <div :class="$style.orgName">{{ organization?.name }}</div>
                        <a v-if="websiteHref" :href="websiteHref" target="_blank" rel="noopener noreferrer" :class="$style.link">
                            {{ websiteLabel }}
                        </a>
                    </div>
                </OrgLogo>

                <div v-if="identifiers.length" :class="$style.identifiers">
                    <div v-for="item in identifiers" :key="item.key" :class="$style.identifierRow">
                        <div :class="$style.identifierText">
                            <span :class="$style.identifierLabel">{{ item.label }}</span>
                            <span :class="$style.identifierValue">{{ item.value }}</span>
                        </div>
                        <!-- PrimeVue hides a tooltip on click, so the icon swaps to a check as the visible confirmation. -->
                        <Button
                            v-tooltip.bottom="copiedKey === item.key ? 'Copied' : 'Copy'"
                            :icon="copiedKey === item.key ? 'pi pi-check' : 'pi pi-copy'"
                            text
                            rounded
                            severity="secondary"
                            :aria-label="item.copyLabel"
                            :class="[$style.copyButton, { [$style.copied]: copiedKey === item.key }]"
                            @click="copy(item.key, item.value)"
                        />
                    </div>
                </div>
            </div>

            <div :class="$style.pane">
                <section v-if="hasContact" :class="$style.section">
                    <h3 :class="$style.sectionTitle">Contact</h3>
                    <div :class="$style.rows">
                        <div v-if="phone" :class="$style.row">
                            <div :class="$style.rowLabel">Phone</div>
                            <div :class="$style.rowValue">{{ phone }}</div>
                        </div>
                        <div v-if="email" :class="$style.row">
                            <div :class="$style.rowLabel">Email</div>
                            <a :href="`mailto:${email}`" :class="[$style.rowValue, $style.rowLink]">{{ email }}</a>
                        </div>
                    </div>
                </section>

                <section v-if="addressLines.length" :class="$style.section">
                    <h3 :class="$style.sectionTitle">Billing address</h3>
                    <div :class="[$style.row, $style.rowTop]">
                        <div :class="$style.rowLabel">Address</div>
                        <div :class="[$style.rowValue, $style.address]">
                            <span v-for="(line, index) in addressLines" :key="index">{{ line }}</span>
                        </div>
                    </div>
                </section>

                <p v-if="!hasContact && !addressLines.length" :class="$style.empty">No contact or billing details on file.</p>
            </div>
        </div>

        <!-- An "Edit profile" primary action belongs here once the admin has an organization edit flow. -->
        <div :class="$style.footer">
            <Button label="Close" outlined severity="secondary" :class="$style.footerButton" @click="close" />
        </div>
    </Dialog>
</template>

<style module>
.dialog {
    width: 780px;
    max-width: calc(100vw - 2rem);
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 24px 64px rgba(15, 23, 42, 0.35);
    overflow: hidden;
}

.content {
    padding: 0;
    display: flex;
    flex-direction: column;
    color: #111827;
}

.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px 18px 28px;
    border-bottom: 1px solid #e5e7eb;
}

.title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #111827;
}

.close {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    color: #5f6b7a;
}

.close :global(.pi) {
    font-size: 18px;
}

.body {
    display: flex;
    align-items: stretch;
}

.rail {
    width: 248px;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 32px 24px;
    background: #f4f6f8;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
    position: relative;
}

.identity {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
}

.orgName {
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    color: #111827;
    overflow-wrap: anywhere;
}

.link {
    font-size: 13px;
    line-height: 18px;
    text-decoration: none;
    color: var(--p-primary-color);
    overflow-wrap: anywhere;
}

.link:hover {
    color: var(--p-primary-600);
    text-decoration: underline;
}

.identifiers {
    margin-top: auto;
    width: 100%;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
}

.identifierRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 28px;
}

.identifierText {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}

.identifierLabel {
    font-size: 10px;
    line-height: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8a94a3;
}

.identifierValue {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 11px;
    line-height: 14px;
    color: #6b7280;
    overflow-wrap: anywhere;
}

.copyButton {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 6px;
    color: #8a94a3;
}

.copyButton :global(.pi) {
    font-size: 13px;
}

.copied,
.copied:hover {
    color: #1f8a4c;
}

.pane {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 28px 28px;
}

.section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sectionTitle {
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #5f6b7a;
}

.rows {
    display: flex;
    flex-direction: column;
}

.row {
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 44px;
    border-bottom: 1px solid #f0f2f4;
}

.rows .row:last-child {
    border-bottom: 0;
}

.rowTop {
    align-items: flex-start;
    padding-top: 8px;
    border-bottom: 0;
}

.rowLabel {
    width: 132px;
    flex-shrink: 0;
    font-size: 13px;
    line-height: 20px;
    color: #5f6b7a;
}

.rowValue {
    flex-grow: 1;
    min-width: 0;
    font-size: 14px;
    color: #111827;
    overflow-wrap: anywhere;
}

.rowLink {
    font-weight: 500;
    text-decoration: none;
    color: var(--p-primary-color);
}

.rowLink:hover {
    color: var(--p-primary-600);
    text-decoration: underline;
}

.address {
    display: flex;
    flex-direction: column;
    line-height: 22px;
}

.empty {
    margin: 0;
    font-size: 14px;
    color: #5f6b7a;
}

.footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 28px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

.footerButton {
    height: 40px;
    padding: 0 16px;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    color: #1f2937;
    font-size: 14px;
    font-weight: 500;
}

.footerButton:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #1f2937;
}

/* Narrow viewports: the rail stacks above the detail pane instead of squeezing it. */
@media (max-width: 640px) {
    .body {
        flex-direction: column;
    }

    .rail {
        width: 100%;
        border-right: 0;
        border-bottom: 1px solid #e5e7eb;
    }

    .row,
    .rowTop {
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        padding: 8px 0;
    }

    .rowLabel {
        width: auto;
    }
}
</style>
